import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/shared";
import { num, fmtDate } from "../lib/api";
import { Printer, ArrowLeft } from "lucide-react";
import { CompanyLogo } from "../components/CompanyLogo";

const REMARK_LINES = [["stitching", "Stitching"], ["washing", "Washing"], ["thread_cutting", "Thread Cutting"], ["other", "Other"]];

function LotRemarksPrint({ order, tid, className = "" }) {
  const l = order?.remark_lines || {};
  return (
    <div className={`text-xs ${className}`} data-testid={tid}>
      <p className="font-semibold text-slate-500 uppercase mb-1">Lot Remarks</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
        {REMARK_LINES.map(([k, label]) => (
          <div key={k} data-testid={`${tid}-${k}`}><span className="text-slate-500">{label} -</span> <span className="font-medium text-slate-800">{l[k] || ""}</span></div>
        ))}
      </div>
      {order?.remarks && <p className="mt-0.5 text-slate-700" data-testid={`${tid}-old`}>{order.remarks}</p>}
    </div>
  );
}

export default function ChallanPrint() {
  const challan = useParams()["*"];
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/challans/${challan}`).then((r) => setData(r.data)).catch(() => {});
  }, [challan]);

  if (!data) return <div className="grid place-items-center h-96"><div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div>;

  const sizes = data.sizes || [];
  const c = data.company || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print">
        <Button variant="secondary" onClick={() => navigate("/challans")}><ArrowLeft size={15} /> Back</Button>
        <Button onClick={() => window.print()} data-testid="print-challan-btn"><Printer size={15} /> Print / Save PDF</Button>
      </div>

      <div id="challan-print" className="bg-white mx-auto shadow-sm border border-slate-200" style={{ maxWidth: "800px", padding: "40px", fontFamily: "Inter, sans-serif" }}>
        <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <CompanyLogo hasLogo={c.has_logo} className="h-14 w-14 rounded-lg bg-white border border-slate-200 p-1" fallback={
              <div className="h-14 w-14 rounded-lg bg-blue-800 text-white grid place-items-center text-2xl font-extrabold" style={{ fontFamily: "Outfit" }}>{(c.name || "A").slice(0, 1)}</div>
            } />
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>{c.name || "ASV APPARELS"}</h1>
              {c.address && <p className="text-xs text-slate-500 max-w-xs">{c.address}</p>}
              <p className="text-xs text-slate-500">{[c.gstin && `GSTIN: ${c.gstin}`, c.phone1, c.phone2, c.email].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-800">DELIVERY CHALLAN</p>
            <p className="text-sm font-mono font-semibold text-blue-800">{data.challan_number}</p>
            <p className="text-xs text-slate-500 mt-1">Date: {fmtDate(data.issue_date || data.created_at)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-5">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Job Worker</p>
            <p className="text-sm font-semibold text-slate-800">{data.jobber_name || "In-House"}</p>
            {data.jobber && (
              <>
                <p className="text-xs text-slate-500">{data.jobber.address}</p>
                <p className="text-xs text-slate-500">Contact: {data.jobber.contact_person} · {data.jobber.phone}</p>
                {data.jobber.gstin && <p className="text-xs text-slate-500">GSTIN: {data.jobber.gstin}</p>}
              </>
            )}
          </div>
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-y-1">
              <span className="text-slate-500">Process</span><span className="font-medium text-slate-800">{data.to_process}</span>
              <span className="text-slate-500">Issue Date</span><span className="font-medium text-slate-800">{fmtDate(data.issue_date || data.created_at)}</span>
              <span className="text-slate-500">Expected Return</span><span className="font-medium text-slate-800">{fmtDate(data.expected_receiving_date)}</span>
              {data.is_multi ? (
                <><span className="text-slate-500">Total Lots</span><span className="font-medium text-slate-800">{data.lots?.length ?? 0}</span></>
              ) : (
                <>
                  <span className="text-slate-500">Production Order</span><span className="font-medium text-slate-800">{data.production_code}</span>
                  <span className="text-slate-500">Design No</span><span className="font-medium text-slate-800">{data.design_number}</span>
                  <span className="text-slate-500">Cutting Sheet No</span><span className="font-medium text-slate-800" data-testid="challan-cutting-sheet">{data.cutting_sheet_number || "—"}</span>
                  <span className="text-slate-500">Cutting Qty</span><span className="font-medium text-slate-800" data-testid="challan-cutting-qty">{data.cutting_qty != null ? `${num(data.cutting_qty)} Pcs` : "—"}</span>
                  <span className="text-slate-500">Colour</span><span className="font-medium text-slate-800">{data.colour}</span>
                  <span className="text-slate-500">Item Type</span><span className="font-medium text-slate-800">{data.item_type}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {data.is_multi ? (
          <div className="mt-5 space-y-4">
            {data.lots.map((lot, li) => (
              <div key={lot.id} className="border border-slate-300 rounded overflow-hidden" data-testid={`challan-lot-${li}`}>
                <div className="flex flex-wrap gap-x-6 gap-y-1 bg-slate-100 px-3 py-2 text-sm border-b border-slate-300">
                  <span className="font-bold text-slate-800">Lot {li + 1}</span>
                  <span><span className="text-slate-500">Design:</span> <b>{lot.design_number || "—"}</b></span>
                  <span><span className="text-slate-500">Prod Order:</span> {lot.production_code || "—"}</span>
                  <span data-testid={`challan-lot-cutting-sheet-${li}`}><span className="text-slate-500">Cutting Sheet:</span> <b>{lot.cutting_sheet_number || "—"}</b></span>
                  <span data-testid={`challan-lot-cutting-qty-${li}`}><span className="text-slate-500">Cutting Qty:</span> {lot.cutting_qty != null ? num(lot.cutting_qty) : "—"}</span>
                  <span><span className="text-slate-500">Colour:</span> {lot.colour || "—"}</span>
                  <span><span className="text-slate-500">Item:</span> {lot.item_type || "—"}</span>
                  <span className="ml-auto"><span className="text-slate-500">Issued Qty:</span> <b className="tabular">{num(lot.total_qty)}</b></span>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase text-slate-500 bg-slate-50">
                    <th className="px-3 py-1.5 text-left border-b border-slate-200">Size</th>
                    <th className="px-3 py-1.5 text-right border-b border-slate-200">Rate (₹/pc)</th>
                    <th className="px-3 py-1.5 text-right border-b border-slate-200">Quantity Issued</th></tr></thead>
                  <tbody>
                    {(lot.sizes || []).map((s, i) => (
                      <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1">{s.size}</td><td className="px-3 py-1 text-right tabular">{lot.issue_rate ? `₹${num(lot.issue_rate)}` : "—"}</td><td className="px-3 py-1 text-right tabular">{num(s.qty)}</td></tr>
                    ))}
                  </tbody>
                </table>
                {lot.accessories?.length > 0 && (
                  <div className="px-3 py-2 border-t border-slate-200 bg-slate-50/60">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Accessories Issued</p>
                    {lot.accessories.map((a) => (
                      <div key={a.id} className="text-xs text-slate-600 flex justify-between"><span>{a.item_name}</span><span className="tabular">{num(a.quantity)} {a.unit}</span></div>
                    ))}
                  </div>
                )}
                <LotRemarksPrint order={lot.order} tid={`challan-lot-remarks-${li}`} className="px-3 py-2 border-t border-slate-200" />
              </div>
            ))}
            <div className="flex justify-end border-t-2 border-slate-800 pt-2 text-sm font-bold text-slate-900" data-testid="challan-grand-total">Grand Total: <span className="tabular ml-2">{num(data.grand_total)} Pcs</span></div>
          </div>
        ) : (
          <>
            <table className="w-full mt-5 border border-slate-300 text-sm">
              <thead>
                <tr className="bg-slate-100 text-xs uppercase text-slate-600">
                  <th className="border border-slate-300 px-3 py-2 text-left">Size</th>
                  <th className="border border-slate-300 px-3 py-2 text-right">Rate (₹/pc)</th>
                  <th className="border border-slate-300 px-3 py-2 text-right">Quantity Issued</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map((s, i) => (
                  <tr key={i}>
                    <td className="border border-slate-300 px-3 py-1.5">{s.size}</td>
                    <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{data.issue_rate ? `₹${num(data.issue_rate)}` : "—"}</td>
                    <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{num(s.qty)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td className="border border-slate-300 px-3 py-2" colSpan={2}>TOTAL ISSUED</td>
                  <td className="border border-slate-300 px-3 py-2 text-right tabular">{num(data.total_qty)}</td>
                </tr>
              </tbody>
            </table>

            {data.accessories?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Accessories Issued</p>
                <table className="w-full border border-slate-300 text-sm">
                  <thead><tr className="bg-slate-100 text-xs uppercase text-slate-600">
                    <th className="border border-slate-300 px-3 py-1.5 text-left">Accessory</th>
                    <th className="border border-slate-300 px-3 py-1.5 text-right">Quantity</th>
                    <th className="border border-slate-300 px-3 py-1.5 text-left">Unit</th></tr></thead>
                  <tbody>{data.accessories.map((a) => (
                    <tr key={a.id}><td className="border border-slate-300 px-3 py-1.5">{a.item_name}</td>
                      <td className="border border-slate-300 px-3 py-1.5 text-right tabular">{num(a.quantity)}</td>
                      <td className="border border-slate-300 px-3 py-1.5">{a.unit}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <LotRemarksPrint order={data.lots?.[0]?.order} tid="challan-lot-remarks" className="mt-4 border border-slate-300 rounded px-3 py-2" />
          </>
        )}

        {data.remarks && <p className="text-sm mt-4 text-slate-600"><span className="font-semibold">Remarks:</span> {data.remarks}</p>}

        <div className="grid grid-cols-3 gap-6 mt-16 text-center text-xs text-slate-600">
          {["Issued By", "Checked By", "Jobber Received By"].map((s) => (
            <div key={s}><div className="border-t border-slate-400 pt-1">{s}</div></div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-6">This is a computer generated challan · {c.name || "ASV APPARELS"}</p>
      </div>
    </div>
  );
}
